/**
 * Checkout floating labels, delivery-first address panels, and order-notes disclosure.
 */

function getConfig() {
	return window.doneCommerce || {};
}

function isFilled(control) {
	if (!control) {
		return false;
	}

	if (control.tagName === 'SELECT') {
		const value = control.value;
		return Boolean(value && value !== '' && value !== null);
	}

	if (control.type === 'checkbox' || control.type === 'radio') {
		return control.checked;
	}

	return String(control.value || '').trim() !== '';
}

function syncFloatingRow(row) {
	if (
		!row ||
		row.closest('.dc-ship-toggle') ||
		row.classList.contains('create-account') ||
		row.classList.contains('dc-marketing-consent') ||
		row.querySelector('input[type="checkbox"], input[type="radio"]')
	) {
		return;
	}

	const control =
		row.querySelector('input.input-text, input[type="email"], input[type="tel"], input[type="text"], input[type="number"], textarea, select') ||
		row.querySelector('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])');

	if (!control) {
		return;
	}

	const filled = isFilled(control);
	row.classList.toggle('is-filled', filled);
	row.classList.toggle('is-floating', filled || document.activeElement === control);
}

function syncAllFloating(root = document) {
	root.querySelectorAll('.dc-checkout .form-row').forEach((row) => syncFloatingRow(row));
}

/**
 * Drop trailing spaces left behind when a required/optional marker is removed.
 * WooCommerce and our sync both append "&nbsp;<span…>", so re-toggles otherwise
 * stack non-breaking spaces and push the asterisk across the field.
 */
function trimLabelMarkerSpace(label) {
	label.normalize();
	for (let index = label.childNodes.length - 1; index >= 0; index -= 1) {
		const node = label.childNodes[index];
		if (node.nodeType !== Node.TEXT_NODE) {
			break;
		}
		node.textContent = node.textContent.replace(/[\u00a0\s]+$/u, '');
		if (node.textContent === '') {
			node.remove();
		}
	}
}

function setAddressRowRequired(row, required) {
	if (!row) {
		return;
	}

	row.classList.toggle('validate-required', required);
	const label = row.querySelector('label');
	if (label) {
		label.querySelectorAll('.optional, .required').forEach((node) => node.remove());
		trimLabelMarkerSpace(label);
		const optionalText = window.wc_address_i18n_params?.i18n_optional_text || 'optional';
		label.insertAdjacentHTML(
			'beforeend',
			required
				? '&nbsp;<span class="required" aria-hidden="true">*</span>'
				: `&nbsp;<span class="optional">(${optionalText})</span>`
		);
	}

	const input = row.querySelector('input, select, textarea');
	if (input) {
		if (required) {
			input.setAttribute('aria-required', 'true');
		} else {
			input.removeAttribute('aria-required');
			input.removeAttribute('required');
		}
	}
}

function syncStreetNumberRequired() {
	['billing', 'shipping'].forEach((prefix) => {
		const street = document.getElementById(`${prefix}_address_1_field`);
		const number = document.getElementById(`${prefix}_street_number_field`);
		if (!street || !number) {
			return;
		}
		setAddressRowRequired(number, street.classList.contains('validate-required'));
	});
}

function ensureOrderNotesDisclosure() {
	const fields = document.querySelector('.dc-checkout .woocommerce-additional-fields');
	if (!fields || fields.querySelector('.dc-order-notes')) {
		return;
	}

	const wrapper = fields.querySelector('.woocommerce-additional-fields__field-wrapper');
	if (!wrapper) {
		return;
	}

	const details = document.createElement('details');
	details.className = 'dc-order-notes';
	const summary = document.createElement('summary');
	summary.innerHTML =
		'<svg class="dc-order-notes__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><path d="M14 3v6h6"></path><path d="M8 13h8"></path><path d="M8 17h5"></path></svg>';
	const label = document.createElement('span');
	label.textContent = getConfig().i18n?.addOrderNote || 'Add a note to your order';
	summary.appendChild(label);
	details.appendChild(summary);
	wrapper.parentNode?.insertBefore(details, wrapper);
	details.appendChild(wrapper);
}

function getWcShipCheckbox() {
	return document.getElementById('ship-to-different-address-checkbox');
}

function getBillingDifferentToggle() {
	return document.querySelector('[data-billing-different]');
}

function getBillingBusinessToggle() {
	return document.querySelector('[data-billing-business]');
}

function getDeliveryMethod() {
	const form = document.querySelector('[data-dc-checkout]');
	const method = form?.dataset.deliveryMethod || '';
	return method === 'collection' || method === 'delivery' ? method : '';
}

function setDeliveryMethod(method) {
	const form = document.querySelector('[data-dc-checkout]');
	if (!form) {
		return;
	}
	form.dataset.deliveryMethod = method;
}

function ensureDeliveryHeading() {
	const shippingFields = document.querySelector('.dc-checkout .woocommerce-shipping-fields');
	if (!shippingFields) {
		return null;
	}

	let heading = shippingFields.querySelector('[data-delivery-title]');
	if (!heading) {
		heading = document.createElement('h3');
		heading.setAttribute('data-delivery-title', '');
		shippingFields.insertBefore(heading, shippingFields.firstChild);
	}
	return heading;
}

function updateHeadings(method, onBillingPanel) {
	const i18n = getConfig().i18n || {};
	const billingHeading = document.querySelector('.dc-checkout .woocommerce-billing-fields > h3');
	const shippingHeading = ensureDeliveryHeading();

	if (method === 'collection') {
		if (billingHeading) {
			billingHeading.textContent = i18n.yourDetails || 'Your details';
			billingHeading.hidden = false;
		}
		if (shippingHeading) {
			shippingHeading.hidden = true;
		}
		return;
	}

	if (shippingHeading) {
		shippingHeading.textContent = i18n.deliveryDetails || 'Delivery details';
		shippingHeading.hidden = false;
	}

	if (billingHeading) {
		billingHeading.textContent = i18n.billingAddress || 'Billing address';
		billingHeading.hidden = !onBillingPanel;
	}
}

function setWcShipDifferent(checked) {
	const wcCheckbox = getWcShipCheckbox();
	if (!wcCheckbox) {
		return;
	}
	if (wcCheckbox.checked !== checked) {
		wcCheckbox.checked = checked;
		wcCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

function forceShippingAddressVisibility(show) {
	const form = document.querySelector('[data-dc-checkout]');
	form?.classList.toggle('is-showing-delivery-address', show);

	document.querySelectorAll('.dc-checkout .woocommerce-shipping-fields .shipping_address').forEach((el) => {
		el.style.removeProperty('display');
		el.style.removeProperty('height');
		el.style.removeProperty('overflow');
		el.style.removeProperty('padding-top');
		el.style.removeProperty('margin-top');
		el.style.removeProperty('border-top-width');
	});
}

function shippingRecipientSaved() {
	return document.querySelector('input[name="dc_shipping_recipient"]')?.value === '1';
}

function copyContactNamesToShipping() {
	if (getDeliveryMethod() === 'collection' || shippingRecipientSaved()) {
		return;
	}

	['first_name', 'last_name'].forEach((key) => {
		const contact = document.getElementById(`dc_contact_${key}`);
		const shipping = document.getElementById(`shipping_${key}`);
		if (!contact || !shipping || shipping.value === contact.value) {
			return;
		}
		shipping.value = contact.value;
	});

	const company = document.getElementById('shipping_company');
	if (company && company.value !== '') {
		company.value = '';
	}
}

/**
 * Move the recipient name and business name under the delivery-modal checkbox.
 */
export function placeShippingRecipientBlock() {
	if (document.getElementById('shipping_first_name_field')?.closest('[data-address-modal]')) {
		return;
	}

	const fields = document.querySelector('[data-shipping-recipient-fields]');
	if (!fields) {
		return;
	}

	['shipping_first_name_field', 'shipping_last_name_field', 'shipping_company_field'].forEach((id) => {
		const field = document.getElementById(id);
		if (field && field.parentElement !== fields) {
			fields.appendChild(field);
		}
	});

	const first = document.getElementById('shipping_first_name_field');
	const last = document.getElementById('shipping_last_name_field');
	const company = document.getElementById('shipping_company_field');
	first?.classList.remove('form-row-wide', 'form-row-last');
	first?.classList.add('form-row-first');
	last?.classList.remove('form-row-wide', 'form-row-first');
	last?.classList.add('form-row-last');
	company?.classList.remove('form-row-first', 'form-row-last', 'dc-checkout-company');
	company?.classList.add('form-row-wide', 'dc-shipping-business');
}

export function syncShippingRecipientRequired() {
	const required = Boolean(document.querySelector('[data-shipping-recipient]')?.checked);
	setAddressRowRequired(document.getElementById('shipping_first_name_field'), required);
	setAddressRowRequired(document.getElementById('shipping_last_name_field'), required);
	setAddressRowRequired(document.getElementById('shipping_company_field'), false);
}

function bindCheckoutSubmitSync() {
	const form = document.querySelector('form.checkout[data-dc-checkout], form.checkout.dc-checkout');
	if (!form || form.dataset.dcAddressSyncBound === 'true') {
		return;
	}

	form.dataset.dcAddressSyncBound = 'true';
	form.addEventListener(
		'submit',
		() => {
			copyContactNamesToShipping();
			setWcShipDifferent(getDeliveryMethod() !== 'collection');
		},
		true
	);
}

function clearBillingBusinessFields() {
	['billing_company', 'billing_abn'].forEach((id) => {
		const input = document.getElementById(id);
		if (!input || String(input.value || '') === '') {
			return;
		}
		input.value = '';
		input.dispatchEvent(new Event('change', { bubbles: true }));
	});
}

/**
 * Checkbox, then Company | ABN side by side, so address lookup can sit under them.
 */
function placeBillingBusinessBlock() {
	if (document.getElementById('billing_company_field')?.closest('[data-address-modal]')) {
		return;
	}
	const billing = document.querySelector('.dc-checkout .woocommerce-billing-fields');
	if (!billing) {
		return;
	}

	const heading = billing.querySelector(':scope > h3');
	const checkbox = billing.querySelector('.dc-billing-business');
	const company = document.getElementById('billing_company_field');
	const abn = document.getElementById('billing_abn_field');

	if (checkbox) {
		const after = heading || billing.firstChild;
		if (heading && checkbox.previousElementSibling !== heading) {
			heading.insertAdjacentElement('afterend', checkbox);
		} else if (!heading && checkbox.parentElement !== billing) {
			billing.insertBefore(checkbox, after);
		}
	}

	let row = billing.querySelector('[data-billing-business-fields]');
	if (!row) {
		row = document.createElement('div');
		row.className = 'dc-billing-business-fields';
		row.setAttribute('data-billing-business-fields', '');
	}

	const afterCheckbox = checkbox || heading;
	if (afterCheckbox && row.previousElementSibling !== afterCheckbox) {
		afterCheckbox.insertAdjacentElement('afterend', row);
	} else if (!row.parentElement) {
		billing.insertBefore(row, billing.firstChild);
	}

	if (company) {
		company.classList.remove('form-row-wide', 'form-row-last');
		company.classList.add('form-row-first', 'dc-checkout-company');
		if (company.parentElement !== row) {
			row.appendChild(company);
		}
	}
	if (abn) {
		abn.classList.remove('form-row-wide', 'form-row-first');
		abn.classList.add('form-row-last', 'dc-checkout-abn');
		if (abn.parentElement !== row) {
			row.appendChild(abn);
		}
	}
}

/**
 * Billing first and last live in the billing modal, under the business checkbox.
 */
export function placeBillingPersonFields() {
	if (document.getElementById('billing_first_name_field')?.closest('[data-address-modal]')) {
		return;
	}

	const anchor = document.querySelector('.dc-checkout .dc-billing-business');
	const first = document.getElementById('billing_first_name_field');
	const last = document.getElementById('billing_last_name_field');
	if (!anchor || !first || !last) {
		return;
	}

	let block = document.querySelector('[data-billing-person-fields]');
	if (!block) {
		block = document.createElement('div');
		block.className = 'dc-billing-person-fields';
		block.setAttribute('data-billing-person-fields', '');
	}
	if (block.previousElementSibling !== anchor) {
		anchor.after(block);
	}

	first.classList.remove('form-row-wide', 'form-row-last', 'dc-checkout-identity');
	first.classList.add('form-row-first');
	last.classList.remove('form-row-wide', 'form-row-first', 'dc-checkout-identity');
	last.classList.add('form-row-last');
	if (first.parentElement !== block) {
		block.appendChild(first);
	}
	if (last.parentElement !== block) {
		block.appendChild(last);
	}
}

function syncBillingCompanyRequired() {
	const required = Boolean(document.querySelector('[data-billing-business]')?.checked);
	setAddressRowRequired(document.getElementById('billing_company_field'), required);
}

export function syncBillingPersonRequired() {
	const modal = document.querySelector('[data-address-modal]');
	const open = Boolean(modal && !modal.hidden && modal.dataset.addressModalFor === 'billing');
	const required = open && !document.querySelector('[data-billing-business]')?.checked;
	['billing_first_name_field', 'billing_last_name_field'].forEach((id) => {
		const row = document.getElementById(id);
		setAddressRowRequired(row, required);
		if (!required) {
			row?.querySelector('input, select, textarea')?.removeAttribute('required');
		}
	});
}

function syncAddressPanels() {
	const method = getDeliveryMethod();
	const deliver = method !== 'collection';
	placeBillingBusinessBlock();
	placeBillingPersonFields();
	placeShippingRecipientBlock();
	syncBillingCompanyRequired();
	syncBillingPersonRequired();
	syncShippingRecipientRequired();
	setWcShipDifferent(deliver);
	forceShippingAddressVisibility(deliver);
	updateHeadings(method, false);
}

function bindBillingDifferentToggle() {
	const themeToggle = getBillingDifferentToggle();
	if (!themeToggle || themeToggle.dataset.bound === 'true') {
		return;
	}

	themeToggle.dataset.bound = 'true';
	themeToggle.addEventListener('change', () => {
		if (!themeToggle.checked) {
			const businessToggle = getBillingBusinessToggle();
			if (businessToggle) {
				businessToggle.checked = false;
			}
			clearBillingBusinessFields();
		}
		syncAddressPanels();
		window.setTimeout(() => syncAllFloating(), 0);
	});
}

function bindBillingBusinessToggle() {
	const businessToggle = getBillingBusinessToggle();
	if (!businessToggle || businessToggle.dataset.bound === 'true') {
		return;
	}

	businessToggle.dataset.bound = 'true';
	businessToggle.addEventListener('change', () => {
		if (!businessToggle.checked) {
			clearBillingBusinessFields();
		}
		syncAddressPanels();
		window.setTimeout(() => syncAllFloating(), 0);
	});
}

function bindFloatingFields(root = document) {
	const checkout = root.querySelector?.('.dc-checkout') || document.querySelector('.dc-checkout');
	if (!checkout) {
		return;
	}

	checkout.querySelectorAll('.form-row').forEach((row) => {
		const control =
			row.querySelector('input.input-text, textarea, select') ||
			row.querySelector('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])');
		if (!control || control.dataset.dcFloatBound === 'true') {
			return;
		}
		control.dataset.dcFloatBound = 'true';
		['focus', 'blur', 'input', 'change'].forEach((evt) => {
			control.addEventListener(evt, () => syncFloatingRow(row));
		});
		syncFloatingRow(row);
	});
}

function bindCouponApply() {
	document.querySelectorAll('[data-dc-coupon]').forEach((root) => {
		if (root.dataset.bound === 'true') {
			return;
		}

		root.dataset.bound = 'true';
		const button = root.querySelector('[data-dc-coupon-apply]');
		const input = root.querySelector('input[name="coupon_code"]');
		const nonce = root.querySelector('[data-dc-coupon-nonce]');

		button?.addEventListener('click', async () => {
			const code = String(input?.value || '').trim();
			if (!code) {
				return;
			}

			button.disabled = true;
			try {
				const params = window.wc_checkout_params || {};
				const endpoint = (params.wc_ajax_url || '').toString().replace('%%endpoint%%', 'apply_coupon');
				if (!endpoint) {
					throw new Error('missing endpoint');
				}

				const body = new URLSearchParams();
				body.set('coupon_code', code);
				body.set('security', nonce?.value || params.apply_coupon_nonce || '');

				const response = await fetch(endpoint, {
					method: 'POST',
					credentials: 'same-origin',
					headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
					body: body.toString(),
				});
				const html = await response.text();

				if (typeof window.jQuery !== 'undefined') {
					const $ = window.jQuery;
					$('.woocommerce-error, .woocommerce-message, .woocommerce-info').remove();
					if (html) {
						$('form.checkout').before(html);
					}
					$(document.body).trigger('update_checkout', { update_shipping_method: false });
				}
			} catch {
				// Checkout update will surface validation if needed.
			} finally {
				button.disabled = false;
			}
		});
	});
}

export function addressRequiredGateways() {
	const list = getConfig().addressRequiredGateways;
	return Array.isArray(list) ? list.map((id) => String(id).toLowerCase()) : ['afterpay'];
}

function selectedPaymentMethod() {
	const checked = document.querySelector('input[name="payment_method"]:checked');
	return String(checked?.value || '').toLowerCase();
}

export function paymentRequiresAddress() {
	const method = selectedPaymentMethod();
	if (!method) {
		return false;
	}
	return addressRequiredGateways().some((id) => method === id || method.includes(id));
}

function syncCollectionAddressVisibility() {
	const form = document.querySelector('[data-dc-checkout]');
	if (!form) {
		return;
	}

	const required = getDeliveryMethod() === 'collection' && paymentRequiresAddress();
	form.classList.toggle('is-billing-address-required', required);
}

function moveContactFields() {
	const slot = document.querySelector('[data-checkout-contact]');
	if (!slot) {
		return;
	}

	const fieldIds = [
		'dc_contact_first_name_field',
		'dc_contact_last_name_field',
		'billing_email_field',
		'billing_phone_field',
	];

	fieldIds.forEach((id) => {
		const field = document.getElementById(id);
		if (field) {
			slot.appendChild(field);
		}
	});

	// Keep marketing consent under the field grid, full width.
	const marketing =
		slot.querySelector('.dc-marketing-consent') ||
		slot.querySelector('.form-row:has(input[type="checkbox"])');
	if (marketing) {
		slot.appendChild(marketing);
	}
}

/**
 * Keep address phone in the postcode row (WC often emits phone as form-row-wide).
 */
function placePhoneBesidePostcode() {
	['shipping', 'billing'].forEach((prefix) => {
		const postcode = document.getElementById(`${prefix}_postcode_field`);
		const phone = document.getElementById(`${prefix}_phone_field`);
		if (!postcode || !phone) {
			return;
		}

		// Billing phone belongs in Contact for delivery; only pair when it stays in address fields.
		if (prefix === 'billing' && phone.closest('[data-checkout-contact]')) {
			return;
		}

		postcode.classList.remove('form-row-wide', 'form-row-last');
		postcode.classList.add('form-row-first', 'address-field');
		phone.classList.remove('form-row-wide', 'form-row-first');
		phone.classList.add('form-row-last', 'dc-checkout-address-phone');

		if (phone.parentElement !== postcode.parentElement || phone.previousElementSibling !== postcode) {
			postcode.insertAdjacentElement('afterend', phone);
		}
	});
}

function syncCollectionPanel() {
	const panel = document.querySelector('[data-collection-details]');
	if (!panel) {
		return;
	}
	panel.hidden = getDeliveryMethod() !== 'collection';
}

function bindCollectionChange() {
	const button = document.querySelector('[data-collection-change]');
	if (!button || button.dataset.bound === 'true') {
		return;
	}

	button.dataset.bound = 'true';
	button.addEventListener('click', () => {
		const cards = document.querySelector('[data-delivery-cards]');
		cards?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		cards?.querySelector('input:checked, input')?.focus();
	});
}

function syncMobileSummary() {
	const details = document.querySelector('[data-mobile-summary]');
	const source = document.querySelector('#order_review .order-total .amount, .order-total .amount');
	document.querySelectorAll('[data-mobile-total]').forEach((el) => {
		el.innerHTML = source ? source.outerHTML : '';
	});

	if (!details) {
		return;
	}

	const desktop = window.matchMedia('(min-width: 1024px)').matches;
	if (desktop) {
		details.open = true;
	}
}

function bindPaymentMethodAddress() {
	const form = document.querySelector('[data-dc-checkout]');
	if (!form || form.dataset.dcAddressGatewayBound === 'true') {
		return;
	}

	form.dataset.dcAddressGatewayBound = 'true';
	form.addEventListener('change', (event) => {
		if (!event.target?.matches?.('input[name="payment_method"]')) {
			return;
		}
		syncCollectionAddressVisibility();
		window.setTimeout(() => syncAllFloating(), 0);
	});
}

export function applyCheckoutDeliveryMethod(method) {
	setDeliveryMethod(method === 'collection' || method === 'delivery' ? method : '');
	moveContactFields();
	placePhoneBesidePostcode();
	syncCollectionPanel();
	syncCollectionAddressVisibility();
	syncAddressPanels();
	bindBillingDifferentToggle();
	bindBillingBusinessToggle();
	bindCheckoutSubmitSync();
	bindCollectionChange();
	bindPaymentMethodAddress();
	bindFloatingFields();
	ensureOrderNotesDisclosure();
	syncMobileSummary();
	syncAllFloating();
}

/**
 * Square keeps the last mounted card as payment_form. Drop earlier orphans.
 */
function keepLastSquareCard() {
	const wrappers = document.querySelectorAll('.payment_method_square_credit_card .sq-card-wrapper');
	if (wrappers.length < 2) {
		return;
	}

	wrappers.forEach((node, index) => {
		if (index < wrappers.length - 1) {
			node.remove();
		}
	});
}

function watchSquareCard() {
	const payment = document.querySelector('#payment');
	if (!payment || payment.dataset.dcSquareWatch === 'true') {
		return;
	}

	payment.dataset.dcSquareWatch = 'true';
	const observer = new MutationObserver(() => {
		keepLastSquareCard();
	});
	observer.observe(payment, { childList: true, subtree: true });
}

export function initCheckoutFields() {
	const checkout = document.querySelector('[data-dc-checkout], .dc-checkout');
	if (!checkout) {
		return;
	}

	ensureOrderNotesDisclosure();
	bindFloatingFields();
	bindBillingDifferentToggle();
	bindBillingBusinessToggle();
	bindCheckoutSubmitSync();
	bindCouponApply();
	bindCollectionChange();
	bindPaymentMethodAddress();
	moveContactFields();
	placePhoneBesidePostcode();
	syncCollectionPanel();
	syncCollectionAddressVisibility();
	syncMobileSummary();
	syncStreetNumberRequired();
	syncAllFloating();

	if (checkout.dataset.deliveryMethod === 'collection' || checkout.dataset.deliveryMethod === 'delivery') {
		applyCheckoutDeliveryMethod(checkout.dataset.deliveryMethod);
	}

	keepLastSquareCard();
	watchSquareCard();

	const onCheckoutUpdated = () => {
		window.requestAnimationFrame(() => {
			keepLastSquareCard();
			watchSquareCard();
			ensureOrderNotesDisclosure();
			bindFloatingFields();
			bindBillingDifferentToggle();
			bindBillingBusinessToggle();
			bindCheckoutSubmitSync();
			bindCouponApply();
			bindPaymentMethodAddress();
			moveContactFields();
			placePhoneBesidePostcode();
			syncCollectionPanel();
			syncCollectionAddressVisibility();
			syncAddressPanels();
			syncMobileSummary();
			syncStreetNumberRequired();
			syncAllFloating();
		});
	};

	// WooCommerce fires this through jQuery, which does not reach native listeners.
	// Address locale marks Street required after paint; Number has to follow that.
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).on('updated_checkout', onCheckoutUpdated);
		window.jQuery(document.body).on('country_to_state_changing country_to_state_changed', () => {
			window.setTimeout(() => {
				syncStreetNumberRequired();
				syncBillingPersonRequired();
				placePhoneBesidePostcode();
				syncAllFloating();
			}, 0);
		});
	}
	document.body.addEventListener('updated_checkout', onCheckoutUpdated);
	window.setTimeout(syncStreetNumberRequired, 0);
	window.setTimeout(syncBillingPersonRequired, 0);
	['billing', 'shipping'].forEach((prefix) => {
		const street = document.getElementById(`${prefix}_address_1_field`);
		if (!street) {
			return;
		}
		const observer = new MutationObserver(() => syncStreetNumberRequired());
		observer.observe(street, { attributes: true, attributeFilter: ['class'] });
	});

	document.addEventListener('dc:address-expanded', () => {
		moveContactFields();
		placePhoneBesidePostcode();
		syncStreetNumberRequired();
		syncAllFloating();
	});

	document.addEventListener('dc:delivery-method', (event) => {
		const method = event?.detail?.method;
		if (method) {
			applyCheckoutDeliveryMethod(method);
		}
		window.setTimeout(syncStreetNumberRequired, 0);
	});
}
